/**
 * SearchableSelect Component
 *
 * A dropdown select with integrated search capability.
 * Type directly in the field to filter options.
 * Uses a portal to render the dropdown above all other elements.
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { cn } from '../../utils/cn';

export interface SearchableSelectOption {
  id: string;
  label: string;
  /** Optional secondary label (e.g., parent category name) */
  sublabel?: string;
}

interface SearchableSelectProps {
  options: SearchableSelectOption[];
  value: string | undefined;
  onChange: (value: string | undefined) => void;
  placeholder?: string;
  emptyLabel?: string;
  disabled?: boolean;
  className?: string;
  /** Show an "All" option at the top to clear the selection */
  showAllOption?: boolean;
  allOptionLabel?: string;
}

export function SearchableSelect({
  options,
  value,
  onChange,
  placeholder,
  emptyLabel,
  disabled,
  className,
  showAllOption = true,
  allOptionLabel,
}: SearchableSelectProps) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredOptions, setFilteredOptions] = useState<SearchableSelectOption[]>([]);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Get the selected option's label for display
  const selectedOption = options.find(opt => opt.id === value);

  // Filter options based on search term
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredOptions(options);
    } else {
      const normalizedSearch = searchTerm.toLowerCase().trim();
      const filtered = options.filter(opt =>
        opt.label.toLowerCase().includes(normalizedSearch) ||
        (opt.sublabel && opt.sublabel.toLowerCase().includes(normalizedSearch))
      );
      setFilteredOptions(filtered);
    }
    setHighlightedIndex(-1);
  }, [searchTerm, options]);

  // Update dropdown position when open
  useEffect(() => {
    if (isOpen && containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + window.scrollY + 4,
        left: rect.left + window.scrollX,
        width: rect.width,
      });
    }
  }, [isOpen]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        // Also check if click is inside the portal dropdown
        const dropdown = document.getElementById('searchable-select-dropdown');
        if (dropdown && dropdown.contains(event.target as Node)) {
          return;
        }
        setIsOpen(false);
        setSearchTerm('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close dropdown on scroll (to avoid mispositioned dropdown)
  useEffect(() => {
    if (!isOpen) return;
    
    const handleScroll = () => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        setDropdownPosition({
          top: rect.bottom + window.scrollY + 4,
          left: rect.left + window.scrollX,
          width: rect.width,
        });
      }
    };
    
    window.addEventListener('scroll', handleScroll, true);
    return () => window.removeEventListener('scroll', handleScroll, true);
  }, [isOpen]);

  // Scroll highlighted item into view
  useEffect(() => {
    if (highlightedIndex >= 0 && listRef.current) {
      const items = listRef.current.querySelectorAll('li');
      const targetIndex = showAllOption ? highlightedIndex + 1 : highlightedIndex;
      items[targetIndex]?.scrollIntoView({ block: 'nearest' });
    }
  }, [highlightedIndex, showAllOption]);

  const handleSelect = useCallback((optionId: string | undefined) => {
    onChange(optionId);
    setIsOpen(false);
    setSearchTerm('');
    inputRef.current?.blur();
  }, [onChange]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    if (!isOpen) {
      setIsOpen(true);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    const totalOptions = filteredOptions.length + (showAllOption ? 1 : 0);

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        if (!isOpen) {
          setIsOpen(true);
        } else {
          setHighlightedIndex(prev =>
            prev < totalOptions - 1 ? prev + 1 : prev
          );
        }
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => (prev > 0 ? prev - 1 : -1));
        break;
      case 'Enter':
        e.preventDefault();
        if (isOpen) {
          if (showAllOption && highlightedIndex === 0) {
            handleSelect(undefined);
          } else {
            const optionIndex = showAllOption ? highlightedIndex - 1 : highlightedIndex;
            if (optionIndex >= 0 && filteredOptions[optionIndex]) {
              handleSelect(filteredOptions[optionIndex].id);
            }
          }
        } else {
          setIsOpen(true);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setSearchTerm('');
        break;
      case 'Tab':
        setIsOpen(false);
        setSearchTerm('');
        break;
    }
  };

  const handleFocus = () => {
    setIsOpen(true);
  };

  // Display value: when typing show search term, otherwise show selected label or placeholder
  const displayValue = isOpen ? searchTerm : (selectedOption?.label || '');

  // Dropdown rendered via portal
  const dropdown = isOpen && !disabled && createPortal(
    <div
      id="searchable-select-dropdown"
      style={{
        position: 'absolute',
        top: dropdownPosition.top,
        left: dropdownPosition.left,
        width: dropdownPosition.width,
        zIndex: 9999,
      }}
      className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-lg overflow-hidden"
    >
      {/* Options list */}
      <ul
        ref={listRef}
        className="max-h-48 overflow-y-auto"
        role="listbox"
      >
        {/* "All" option to clear selection */}
        {showAllOption && (
          <li
            onClick={() => handleSelect(undefined)}
            onMouseEnter={() => setHighlightedIndex(0)}
            className={cn(
              'px-3 py-2 cursor-pointer text-sm',
              highlightedIndex === 0
                ? 'bg-violet-100 dark:bg-violet-900/50 text-violet-900 dark:text-violet-100'
                : 'text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700'
            )}
            role="option"
            aria-selected={!value}
          >
            {allOptionLabel || t('operations.filter.all', 'Tous')}
          </li>
        )}

        {/* Filtered options */}
        {filteredOptions.length > 0 ? (
          filteredOptions.map((option, index) => {
            const listIndex = showAllOption ? index + 1 : index;
            return (
              <li
                key={option.id}
                onClick={() => handleSelect(option.id)}
                onMouseEnter={() => setHighlightedIndex(listIndex)}
                className={cn(
                  'px-3 py-2 cursor-pointer text-sm',
                  highlightedIndex === listIndex
                    ? 'bg-violet-100 dark:bg-violet-900/50 text-violet-900 dark:text-violet-100'
                    : 'text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700',
                  value === option.id && 'font-medium'
                )}
                role="option"
                aria-selected={value === option.id}
              >
                <div>{option.label}</div>
                {option.sublabel && (
                  <div className="text-xs text-slate-400 dark:text-slate-500">
                    {option.sublabel}
                  </div>
                )}
              </li>
            );
          })
        ) : (
          <li className="px-3 py-2 text-sm text-slate-500 dark:text-slate-400">
            {emptyLabel || t('common.noResults', 'Aucun résultat')}
          </li>
        )}
      </ul>
    </div>,
    document.body
  );

  return (
    <div ref={containerRef} className={cn('relative', className)}>
      {/* Input field - shows selected value or search term */}
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={displayValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={handleFocus}
          placeholder={placeholder || t('common.select', 'Sélectionner...')}
          disabled={disabled}
          className={cn(
            'w-full px-2 py-1.5 pr-7 text-sm border rounded bg-white dark:bg-slate-700 focus:outline-none focus:ring-1 focus:ring-violet-500 focus:border-violet-500',
            'border-slate-300 dark:border-slate-600 text-slate-900 dark:text-slate-100',
            !displayValue && !isOpen && 'text-slate-400 dark:text-slate-500',
            disabled && 'opacity-50 cursor-not-allowed'
          )}
          autoComplete="off"
          aria-autocomplete="list"
          aria-expanded={isOpen}
          role="combobox"
        />
        {/* Dropdown arrow */}
        <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
          <svg
            className={cn('w-4 h-4 text-slate-400 transition-transform', isOpen && 'rotate-180')}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
        {/* Clear button when value is selected */}
        {value && !isOpen && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              handleSelect(undefined);
            }}
            className="absolute inset-y-0 right-6 flex items-center px-1 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>

      {/* Dropdown rendered via portal */}
      {dropdown}
    </div>
  );
}

export default SearchableSelect;
